import React, { FC } from 'react';

import { AttachmentAction, AttachmentMode } from '../../../models/attachment';
import ButtonAttachmentAction from '../../ButtonAttachmentAction';
import ButtonListItemDelete from '../../Common/ButtonListItemDelete';
import Spinner from '../../Common/Spinner';

import { DetailsEditFragmentWrapper as StyledWrapper } from './styled';

// Modes in which the user can see the delete button
const DELETABLE_MODES = new Set(['compose', 'detailsEdit']);
const ICON_SIZE = 20;
const SPINNER_SIZE = 18;

type CommonProps = {
  isArchived?: boolean;
  isUploading?: boolean;
  mode: AttachmentMode;
  requestButtonClick: () => void;
};

export type Props = CommonProps &
  (
    | {
        actionDownload?: AttachmentAction;
        canDownloadFile: false;
      }
    | {
        actionDownload: AttachmentAction;
        canDownloadFile: true;
      }
  );

/**
 * Fragment of AttachmentSingle rendered in detailsEdit mode
 *
 * @param props                    Props passed to the component
 * @param props.actionDownload     Attachment action download props
 * @param props.canDownloadFile    Whether the user can download the file
 * @param props.isArchived         Whether the attachment is archived
 * @param props.isUploading        Whether the attachment is being uploaded
 * @param props.mode               In which mode to render the component
 * @param props.requestButtonClick Callback for delete button click
 * @returns                        The component itself
 */
const DetailsEditFragment: FC<Props> = ({
  actionDownload,
  canDownloadFile,
  isArchived,
  isUploading = false,
  mode,
  requestButtonClick,
}) => {
  if (mode !== 'detailsEdit') {
    return null;
  }

  if (isUploading) {
    return <Spinner size={SPINNER_SIZE} />;
  }

  /**
   * Since the Draft Message does not have the message object passed, we cannot use getCanDeleteAttachment(message);
   * Instead, we determine whether to show the delete button based on the mode.
   */

  const shouldShowDeleteButton = !isArchived && DELETABLE_MODES.has(mode);

  return (
    <StyledWrapper>
      {canDownloadFile && (
        <ButtonAttachmentAction
          color="var(--color-icons-primary)"
          iconSize={ICON_SIZE}
          isInProgress={actionDownload.isInProgress}
          link={actionDownload.link}
          linkRef={actionDownload.linkRef}
          requestButtonClick={actionDownload.requestButtonClick}
        />
      )}
      {shouldShowDeleteButton && (
        <ButtonListItemDelete onClick={requestButtonClick} />
      )}
    </StyledWrapper>
  );
};

export default DetailsEditFragment;
